import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../utils/apiClient';
import { Event } from '../types';

const PublicEventsPage: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/v1/events', {
        params: { status: 'public' }
      });
      setEvents(response.data.data?.events || []);
    } catch (err: any) {
      console.error('Error fetching events:', err);
      setError(err.response?.data?.error?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter(event => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      event.name.toLowerCase().includes(query) ||
      event.location.toLowerCase().includes(query) ||
      event.description.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          <i className="fas fa-calendar-alt me-2"></i>
          Public Events
        </h2>
        <Link to="/sessions/new" className="btn btn-primary">
          <i className="fas fa-plus me-2"></i>
          Propose Session
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body">
          <div className="input-group">
            <span className="input-group-text">
              <i className="fas fa-search"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search events by name, location, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="fas fa-calendar-alt fa-3x text-muted mb-3"></i>
            <h4>No Public Events Found</h4>
            <p className="text-muted">
              {searchQuery ? 'Try adjusting your search criteria.' : 'There are no public events available at this time.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="row">
          {filteredEvents.map(event => (
            <div key={event.event_id} className="col-md-6 mb-4">
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="card-title">{event.name}</h5>
                  <p className="text-muted mb-2">
                    <i className="fas fa-map-marker-alt me-1"></i>
                    {event.location}
                  </p>
                  <p className="text-muted mb-2">
                    <i className="fas fa-calendar me-1"></i>
                    {new Date(event.start_date).toLocaleDateString()} -{' '}
                    {new Date(event.end_date).toLocaleDateString()}
                  </p>
                  <p className="card-text">{event.description}</p>
                  {event.creator_name && (
                    <p className="text-muted small mb-0">
                      <i className="fas fa-user me-1"></i>
                      Organized by {event.creator_name}
                    </p>
                  )}
                </div>
                <div className="card-footer bg-white border-top-0">
                  <div className="d-flex gap-2">
                    <Link
                      to={`/events/${event.event_id}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      <i className="fas fa-eye me-1"></i>
                      View Details
                    </Link>
                    <Link
                      to={`/sessions/new?event=${event.event_id}`}
                      className="btn btn-sm btn-primary"
                    >
                      <i className="fas fa-paper-plane me-1"></i>
                      Propose Session
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublicEventsPage;
